'use client';

import { useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { cn } from '@kit/ui/utils';

import { setActiveRoleAction } from '../_lib/server/set-active-role.action';

interface RoleSwitcherProps {
  roles: Array<{ profileType: string; profileId: string }>;
  activeRole: string | null;
  /**
   * Flag-derived roles the user can also act as (super admin, competition
   * partner). These have no professional_profiles row, so they arrive with an
   * explicit translation key rather than the onboarding role-label lookup.
   */
  extraRoles?: Array<{ value: string; labelKey: string }>;
  className?: string;
}

/**
 * App-shell control letting a multi-role user switch the role they are acting
 * as. Renders nothing for single-role users. Writes the choice via
 * setActiveRoleAction (ballee-active-role cookie) and refreshes so the server
 * re-resolves role-dependent navigation. See BAL-340.
 */
export function RoleSwitcher({
  roles,
  activeRole,
  extraRoles = [],
  className,
}: RoleSwitcherProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  // Flag-derived roles lead (Admin · Client · Partner), then profile roles
  // (2026-07-05 order feedback). Deduped, order-preserving.
  const entries: Array<{ value: string; label: string }> = [];
  const seen = new Set<string>();

  for (const extra of extraRoles) {
    if (seen.has(extra.value)) continue;
    seen.add(extra.value);
    entries.push({ value: extra.value, label: t(extra.labelKey) });
  }

  for (const role of roles) {
    if (seen.has(role.profileType)) continue;
    seen.add(role.profileType);
    entries.push({
      value: role.profileType,
      label: t(
        `onboarding:welcome.rolePicker.additionalRoles.${role.profileType}`,
        { defaultValue: role.profileType },
      ),
    });
  }

  if (entries.length < 2) {
    return null;
  }

  // Displayed default mirrors resolveActingRole (acting-role.ts): superadmin
  // when held, else the first PROFILE role — not the first list entry, since
  // extras now lead the list.
  const fallbackRole = seen.has('superadmin')
    ? 'superadmin'
    : (roles.find((r) => seen.has(r.profileType))?.profileType ??
      entries[0]!.value);
  const current =
    activeRole && seen.has(activeRole) ? activeRole : fallbackRole;

  const onSelect = (role: string) => {
    if (role === current) return;
    startTransition(async () => {
      const result = await setActiveRoleAction({ role });
      if (result.success) {
        router.refresh();
      }
    });
  };

  const currentLabel =
    entries.find((e) => e.value === current)?.label ?? current;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={isPending}
          className={cn('gap-1.5', className)}
          aria-label={t('account:roleSwitcher.switchRole')}
        >
          <span className="text-muted-foreground text-xs">
            {t('account:roleSwitcher.actingAs')}
          </span>
          <span className="font-medium">{currentLabel}</span>
          <ChevronsUpDown className="text-muted-foreground h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuLabel>
          {t('account:roleSwitcher.switchRole')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={current} onValueChange={onSelect}>
          {entries.map((entry) => (
            <DropdownMenuRadioItem key={entry.value} value={entry.value}>
              {entry.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
